import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db, handleFirestoreError, OperationType } from '../../firebase';
import { motion } from 'framer-motion';
import { 
  Ticket, 
  MessageSquare, 
  Clock, 
  CheckCircle2, 
  AlertCircle,
  ArrowRight
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';

export const Dashboard: React.FC = () => {
  const { profile } = useAuth();
  const [recentTickets, setRecentTickets] = useState<any[]>([]);
  const [stats, setStats] = useState({ open: 0, resolved: 0, messages: 0 });

  useEffect(() => {
    if (!profile) return;

    // Fetch recent tickets
    const ticketsQuery = query(
      collection(db, 'tickets'),
      where('clientId', '==', profile.uid),
      where('tenantId', '==', profile.tenantId || ''),
      orderBy('createdAt', 'desc'),
      limit(5)
    );

    const unsubTickets = onSnapshot(ticketsQuery, (snapshot) => {
      const tickets = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setRecentTickets(tickets);
      
      // Calculate stats (in a real app, you might use a separate query or cloud function)
      const open = tickets.filter((t: any) => t.status !== 'resolved' && t.status !== 'closed').length;
      const resolved = tickets.filter((t: any) => t.status === 'resolved').length;
      setStats(prev => ({ ...prev, open, resolved }));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'tickets');
    });

    return () => unsubTickets();
  }, [profile]);

  const statCards = [
    { label: 'Active Requests', value: stats.open, icon: Clock, color: 'text-blue-500', bg: 'bg-blue-500/10' },
    { label: 'Resolved Tickets', value: stats.resolved, icon: CheckCircle2, color: 'text-green-500', bg: 'bg-green-500/10' },
    { label: 'Unread Messages', value: 0, icon: MessageSquare, color: 'text-purple-500', bg: 'bg-purple-500/10' },
  ];

  return (
    <div className="space-y-10">
      {/* Welcome Header */}
      <section>
        <h1 className="text-3xl font-bold tracking-tight mb-2">Welcome back, {profile?.displayName?.split(' ')[0]}</h1>
        <p className="text-gray-400">Here's what's happening with your support requests.</p>
      </section>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {statCards.map((stat, i) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            className="bg-[#0A0A0A] border border-white/5 p-6 rounded-3xl"
          >
            <div className={`w-24 h-24 ${stat.bg} rounded-2xl flex items-center justify-center mb-4`}>
              <stat.icon className={stat.color} size={24} />
            </div>
            <p className="text-gray-500 text-xs font-mono uppercase tracking-widest mb-1">{stat.label}</p>
            <p className="text-3xl font-bold">{stat.value}</p>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        {/* Recent Tickets */}
        <section className="bg-[#0A0A0A] border border-white/5 rounded-3xl overflow-hidden">
          <div className="p-6 border-b border-white/5 flex items-center justify-between">
            <h3 className="font-bold flex items-center gap-2">
              <Ticket size={18} className="text-blue-500" />
              Recent Requests
            </h3>
            <Link to="/tickets" className="text-xs text-blue-500 hover:underline flex items-center gap-1">
              View All <ArrowRight size={12} />
            </Link>
          </div>
          <div className="divide-y divide-white/5">
            {recentTickets.length > 0 ? (
              recentTickets.map((ticket) => (
                <div key={ticket.id} className="p-6 hover:bg-white/[0.02] transition-colors">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-medium text-sm">{ticket.subject}</h4>
                    <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-md border ${
                      ticket.status === 'open' ? 'border-blue-500/30 text-blue-500 bg-blue-500/5' :
                      ticket.status === 'in-progress' ? 'border-yellow-500/30 text-yellow-500 bg-yellow-500/5' :
                      'border-green-500/30 text-green-500 bg-green-500/5'
                    }`}>
                      {ticket.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <Clock size={12} />
                      {formatDistanceToNow(new Date(ticket.createdAt))} ago
                    </span>
                    <span className="w-1 h-1 bg-white/10 rounded-full"></span>
                    <span className="uppercase tracking-wider">{ticket.category}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-10 text-center">
                <p className="text-gray-500 text-sm">No recent requests found.</p>
              </div>
            )}
          </div>
        </section>

        {/* Announcements / Notifications */}
        <section className="bg-[#0A0A0A] border border-white/5 rounded-3xl overflow-hidden">
          <div className="p-6 border-b border-white/5">
            <h3 className="font-bold flex items-center gap-2">
              <AlertCircle size={18} className="text-purple-500" />
              System Updates
            </h3>
          </div>
          <div className="p-8 space-y-6">
            <div className="bg-purple-500/5 border border-purple-500/10 p-4 rounded-2xl">
              <p className="text-xs font-mono text-purple-500 uppercase tracking-widest mb-2">New Feature</p>
              <h4 className="font-bold text-sm mb-1">Real-time Messaging Enabled</h4>
              <p className="text-xs text-gray-400 leading-relaxed">You can now chat directly with our support team for faster resolution of your requests.</p>
            </div>
            <div className="bg-blue-500/5 border border-blue-500/10 p-4 rounded-2xl">
              <p className="text-xs font-mono text-blue-500 uppercase tracking-widest mb-2">Maintenance</p>
              <h4 className="font-bold text-sm mb-1">Scheduled System Update</h4>
              <p className="text-xs text-gray-400 leading-relaxed">We will be performing routine maintenance on Sunday at 2:00 AM UTC. Expect brief downtime.</p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};
